/**
 * White-Label Branding Utility
 * 
 * Applies custom branding (colors, logo) for white-label organizations.
 * Only applies when whiteLabelEnabled = true.
 */

/**
 * Apply white-label branding to the app
 * @param {Object} branding - Branding configuration
 * @param {String} branding.primaryColor - Primary brand color
 * @param {String} branding.secondaryColor - Secondary brand color
 * @param {String} branding.backgroundColor - Background color
 * @param {String} branding.textColor - Text color
 * @param {String} branding.accentColor - Accent color
 * @param {String} branding.logo - Logo URL
 */
export const applyWhiteLabelBranding = (branding) => {
  if (!branding) {
    console.warn('⚠️ applyWhiteLabelBranding called with no branding');
    return;
  }
  
  console.log('🎨 applyWhiteLabelBranding called with:', branding);
  
  const root = document.documentElement;
  const body = document.body;
  
  if (!root || !body) {
    console.error('❌ DOM not ready - root or body is null');
    return;
  }
  
  // Add data attribute for CSS targeting
  body.setAttribute('data-white-label', 'true');
  console.log('✅ Set data-white-label attribute');
  
  // Apply color variables (use existing luxury tokens or create new ones)
  if (branding.primaryColor) {
    root.style.setProperty('--luxury-gold-muted', branding.primaryColor);
    root.style.setProperty('--color-primary', branding.primaryColor);
    root.style.setProperty('--ev-primary', branding.primaryColor);
    root.style.setProperty('--gold', branding.primaryColor);
    console.log('✅ Set primaryColor:', branding.primaryColor);
  }
  if (branding.secondaryColor) {
    root.style.setProperty('--luxury-aubergine', branding.secondaryColor);
    root.style.setProperty('--color-background', branding.secondaryColor);
    root.style.setProperty('--ev-secondary', branding.secondaryColor);
    console.log('✅ Set secondaryColor:', branding.secondaryColor);
  }
  if (branding.backgroundColor) {
    root.style.setProperty('--luxury-bg-primary', branding.backgroundColor);
    root.style.setProperty('--ev-background', branding.backgroundColor);
    root.style.setProperty('--color-background', branding.backgroundColor);
    // Also apply directly to body for immediate effect
    body.style.backgroundColor = branding.backgroundColor;
    console.log('✅ Set backgroundColor:', branding.backgroundColor);
  }
  if (branding.textColor) {
    root.style.setProperty('--luxury-text-primary', branding.textColor);
    root.style.setProperty('--color-text', branding.textColor);
    root.style.setProperty('--ev-text', branding.textColor);
    // Also apply directly to body
    body.style.color = branding.textColor;
    console.log('✅ Set textColor:', branding.textColor);
  }
  if (branding.accentColor) {
    root.style.setProperty('--luxury-accent', branding.accentColor);
    root.style.setProperty('--ev-accent', branding.accentColor);
    console.log('✅ Set accentColor:', branding.accentColor);
  }
  
  // Apply logo (support both 'logo' and 'logoUrl' keys)
  const logoUrl = branding.logo || branding.logoUrl;
  if (logoUrl) {
    const logoElements = document.querySelectorAll('.app-logo, .logo, [data-logo]');
    console.log(`🔍 Found ${logoElements.length} logo elements`);
    logoElements.forEach(el => {
      if (el.tagName === 'IMG') {
        el.src = logoUrl;
        console.log('✅ Updated IMG logo src:', logoUrl);
      } else {
        el.style.backgroundImage = `url(${logoUrl})`;
        console.log('✅ Updated logo background-image:', logoUrl);
      }
    });
  } else {
    console.log('⚠️ No logo URL provided');
  }
  
  console.log('✅ Branding application complete');
};

/**
 * Remove white-label branding (restore defaults)
 */
export const removeWhiteLabelBranding = () => {
  const root = document.documentElement;
  const body = document.body;
  
  // Remove data attribute
  body.removeAttribute('data-white-label');
  
  // Remove CSS variables
  root.style.removeProperty('--ev-primary');
  root.style.removeProperty('--ev-secondary');
  root.style.removeProperty('--ev-background');
  root.style.removeProperty('--ev-text');
  root.style.removeProperty('--ev-accent');
  
  // Remove direct body styles
  body.style.removeProperty('background-color');
  body.style.removeProperty('color');
};

/**
 * Get white-label branding from family
 * @param {Object} family - Family object
 * @returns {Object|null} Branding config or null
 */
export const getWhiteLabelBranding = (family) => {
  if (!family?.whiteLabelEnabled || !family?.whiteLabelBranding) {
    return null;
  }
  return family.whiteLabelBranding;
};

